import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PORTFOLIO_ITEMS } from '../data/portfolioData';

const ORIGINALS_LIST = PORTFOLIO_ITEMS.filter((item) => item.category === 'originals');

export default function OriginalsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const activeFilm = ORIGINALS_LIST[activeIndex] || ORIGINALS_LIST[0];

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    v.play().catch(() => {});
  }, [activeFilm.videoUrl]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.muted = isMuted;
  }, [isMuted]);

  return (
    <section
      id="originals"
      className="bg-black py-20 sm:py-28 border-t border-white/[0.08] w-full"
    >
      <div className="w-full px-5 sm:px-10 lg:px-16 xl:px-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-8 border-b border-white/[0.08]">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-[#E50914] mb-2">
              ● 100% AI-Native Original IP &nbsp;·&nbsp; Short Films &amp; Music Videos
            </p>
            <h2 className="text-white font-normal tracking-[-0.03em] leading-[1.05] text-[clamp(1.85rem,3.4vw,3.25rem)]">
              MotionX Originals &amp; Music Videos
            </h2>
          </div>
          <p className="text-[#767d88] text-sm sm:text-[15px] max-w-md">
            Original cinema and official music videos developed and produced in-house by
            MotionX Studios.
          </p>
        </div>

        {/* Widescreen Stage */}
        <div className="mt-8 relative rounded-xl overflow-hidden border border-white/[0.08] bg-[#070709] min-h-[480px] lg:min-h-[560px] flex flex-col justify-end p-6 sm:p-10 lg:p-14">
          <video
            ref={videoRef}
            key={activeFilm.videoUrl}
            src={activeFilm.videoUrl}
            poster={activeFilm.posterUrl}
            autoPlay
            muted={isMuted}
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.82) contrast(1.05)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 via-[38%] to-transparent pointer-events-none" />

          {/* Top-right Sound Toggle */}
          <div className="absolute top-5 right-5 z-20 flex items-center gap-2">
            {activeFilm.highlight && (
              <span className="px-3 py-1.5 rounded-full bg-[#E50914] text-white text-[11px] font-medium shadow-[0_0_20px_rgba(229,9,20,0.4)]">
                ★ Featured Original
              </span>
            )}
            <button
              type="button"
              onClick={() => setIsMuted((m) => !m)}
              className="px-4 py-2 rounded-full bg-black/65 backdrop-blur-md border border-white/15 text-white text-xs cursor-pointer hover:bg-white hover:text-black transition-colors"
            >
              {isMuted ? 'Unmute' : 'Sound On'}
            </button>
          </div>

          {/* Bottom-left Film Info */}
          <div className="relative z-10 max-w-2xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFilm.slug}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-[11px] uppercase tracking-[0.14em] text-white/70 mb-2">
                  {activeFilm.subType} &nbsp;·&nbsp; {activeFilm.productionMode}
                </p>
                <h3 className="text-white font-normal tracking-[-0.035em] text-3xl sm:text-5xl leading-[1.02]">
                  {activeFilm.title}
                </h3>
                <p className="text-[#a1a7b3] text-sm sm:text-[15px] leading-relaxed mt-3 max-w-xl">
                  {activeFilm.description}
                </p>
                {activeFilm.watchUrl && (
                  <a
                    href={activeFilm.watchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full bg-white text-black text-xs font-medium no-underline"
                  >
                    Watch Full Film on YouTube ↗
                  </a>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* 6-Original Thumbnail Selector Row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-5">
          {ORIGINALS_LIST.map((film, i) => {
            const active = i === activeIndex;
            return (
              <button
                key={film.slug}
                type="button"
                onClick={() => setActiveIndex(i)}
                className={`group text-left cursor-pointer transition-opacity ${
                  active ? 'opacity-100' : 'opacity-55 hover:opacity-90'
                }`}
              >
                <div
                  className={`aspect-video rounded-lg overflow-hidden bg-[#111113] mb-2 border transition-colors relative ${
                    active ? 'border-[#E50914]' : 'border-white/10'
                  }`}
                >
                  <img
                    src={film.posterUrl}
                    alt={film.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  {film.highlight && (
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-[#E50914] text-white text-[9px] font-medium">
                      ★ Featured
                    </span>
                  )}
                </div>
                <p className="text-[14px] font-medium tracking-[-0.01em] text-white truncate">
                  {film.title}
                </p>
                <p className="text-[12px] text-[#767d88] truncate">{film.subType}</p>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
